/**
 * `companies.locations` is a jsonb list; `companies.headquarters_location` is a
 * plain string, and the two describe the same fact.
 *
 * The settings page edits one city per row, but a stored location carries six
 * fields — `text`, `city`, `state`, `country`, `countryCode`, `headquarter`.
 * Everything here exists so that editing the one visible field never destroys
 * the five that are not (EC7), and so that the two columns converge instead of
 * drifting apart (I4).
 */

import type { CompanyLocation } from "@/domains/company/onboarding/types";

/**
 * A row of the Sedi list as the section holds it.
 *
 * `origin` is the entry the server sent, kept whole. Without it the save would
 * rebuild each location from what is on screen, and "Los Gatos, CA, United
 * States" would shrink to "Los Gatos" — silently, at every save, on data the
 * user never touched.
 */
export type LocationRow = {
  city: string;
  headquarter: boolean;
  origin?: CompanyLocation;
};

export type LocationsPatch = {
  locations: CompanyLocation[];
  /** Realigned to the headquarter's city on every write (AC 17). */
  headquartersLocation: string;
};

/**
 * The city inside a formatted location line.
 *
 * `text` is what the import stored for display — "Los Gatos, CA, United
 * States" — and the city is its head. Everything downstream treats this value
 * as a city and nothing else: the input is labelled Città, the public identity
 * card prints it under a pin, and every job offer shows it as «Sede
 * principale». Handing over the whole line puts a region and a country in that
 * one spot; keeping the head narrows a value already given rather than
 * inventing one.
 *
 * The line is only ever a fallback — a location with a `city` field never gets
 * here — so this runs on data an import wrote without parsing it, and on
 * `headquarters_location` rows written before that column meant a city.
 */
const cityPart = (text: string) => {
  const [head] = text.split(",");
  return head?.trim() || text.trim();
};

/** What the section shows for a stored location: its city, or the head of the line. */
const cityOf = (entry: CompanyLocation) =>
  entry.city?.trim() || cityPart(entry.text);

const entryOf = (row: LocationRow): CompanyLocation => {
  const city = row.city.trim();
  // Untouched: back exactly as it arrived, unread fields included.
  if (row.origin && cityOf(row.origin) === city) return { ...row.origin };
  // New — or renamed, which here is the same thing. `state`, `country` and
  // `countryCode` describe the place that used to be on this row; carrying them
  // over would assert "Milano, CA, United States", a fact nobody stated. The
  // city is all the user said, so `text` is derived from it: a reformatting of
  // a value already given, not an invention (FLOW-F6).
  return { text: city, city, headquarter: false };
};

/**
 * Turns the rows on screen into the two columns that leave.
 *
 * Three rules, all of them consequences of the list being editable while only
 * part of it is visible:
 *
 * - a row without a city is **not saved** — a state, not an error (EC4). Which
 *   also means a saved row emptied out is a removal, and the section says so;
 * - exactly one headquarter leaves, whatever the rows claim. The flag can sit
 *   on a row that will not be saved (EC6), or on none of them — an import can
 *   mark none — and both would otherwise empty `headquarters_location`, the
 *   line every job offer shows next to the company name;
 * - `headquarters_location` is rewritten from the headquarter's city, so the
 *   two columns cannot disagree after a save (I4/AC 17).
 */
export const deriveLocationsPatch = (
  rows: readonly LocationRow[],
): LocationsPatch => {
  const savable = rows.filter((row) => row.city.trim() !== "");
  const marked = savable.findIndex((row) => row.headquarter);
  const headquarterIndex = marked === -1 ? 0 : marked;

  return {
    locations: savable.map((row, index) => ({
      ...entryOf(row),
      headquarter: index === headquarterIndex,
    })),
    // The row's city, which `cityOf` already narrowed to one: writing `text`
    // would put "Los Gatos, CA, United States" in a column the public identity
    // card prints as a city (MAJOR v7).
    headquartersLocation: savable[headquarterIndex]?.city.trim() ?? "",
  };
};

/**
 * The rows the section opens with.
 *
 * A company created by hand has `headquarters_location` and no `locations`. An
 * empty list would read as "no location" to someone who declared one during
 * onboarding, and they would add it a second time — so that value seeds the
 * first row. Convergence I4 seen from the outside: not a new field, the field
 * from before, now in a list (EC5).
 */
export const seedLocationRows = (company: {
  locations?: readonly CompanyLocation[] | null;
  headquarters_location?: string | null;
}): LocationRow[] => {
  const stored = company.locations ?? [];
  if (stored.length > 0)
    return (
      stored
        .map((entry) => ({
          city: cityOf(entry),
          headquarter: entry.headquarter,
          origin: entry,
        }))
        // A stored entry whose city comes out empty is not a row. Only `text` is
        // required on `CompanyLocation`, the import writes the scraper's array
        // verbatim and nothing validates it on the way back, so `{text: "   "}`
        // is storable — and a surface that trusts the raw length prints a heading,
        // a «1 città» count and a blank line under it, the orphan heading AC 6
        // forbids. `deriveLocationsPatch` already refuses to save such a row, so
        // dropping it here only stops showing what the next save would remove
        // anyway: the same rule, applied on the way in as well as on the way out.
        .filter((row) => row.city !== "")
    );

  // Narrowed like a stored line, and for the same reason: that column holds
  // "Milano, Lombardia, Italia" on every company the import touched, and the
  // row seeded from it goes into an input labelled Città.
  const headquarters = cityPart(company.headquarters_location ?? "");
  return headquarters === "" ? [] : [{ city: headquarters, headquarter: true }];
};

/**
 * The one city a surface prints next to the company name.
 *
 * Reading `headquarters_location` directly instead is the mistake this exists
 * to stop. That column is not a city on every row: `resolveHeadquartersLabel`
 * writes the import's whole formatted line into it — "Milano, Lombardia,
 * Italia" — and nothing narrows it afterwards, because the only code that does
 * (`deriveLocationsPatch`) runs on **save**, so it never touches a company
 * whose Sedi card the owner has not opened. A surface that prints the column
 * raw and a surface that seeds rows from it therefore disagree about the same
 * fact, and the disagreement is visible: three spellings of one address across
 * the public page, the offer's company sub-sheet and its Sedi section.
 *
 * Built on `seedLocationRows` rather than beside it so the answer cannot drift
 * from the list the Sedi section shows: same fallback, same narrowing, same
 * choice of which row is the principal one.
 */
export const headquartersCity = (company: {
  locations?: readonly CompanyLocation[] | null;
  headquarters_location?: string | null;
}): string | undefined => {
  const rows = seedLocationRows(company);
  const principal = rows.find((row) => row.headquarter) ?? rows[0];
  return principal?.city || undefined;
};
